import styled from '@emotion/styled';
import React, { useMemo } from 'react';
import { Accordion } from 'react-bootstrap';
import { Card } from 'react-bootstrap';
import Select, { components } from 'react-select';
import { BsSearch as Search } from 'react-icons/bs';
import BayBadge from '../../BayBadge';
import type { VoidCallback } from '../../types/callbacks';
import type { TranslationFunction } from '../../types/common';

import '../styles/settingsMenu.scss';

interface SettingsOptions {
	accountType?: string;
	appSettings?: Record<string, any>;
	isAutomationAvailable?: boolean;
	isEnterprise?: boolean;
	isEnterpriseChild?: boolean;
	isFbLimited?: boolean;
	ownBilling?: boolean;
	showLandingPages?: boolean;
	reqAccess?: Record<string, any>;
	settingItems?: any[];
	user?: Record<string, any>;
	showAnnouncementBanners?: boolean;
}

interface SettingsMenuProps {
	settingsOptions?: SettingsOptions;
	t?: TranslationFunction;
	platformType?: string;
	onClose?: VoidCallback;
	toggleCloseFunc?: VoidCallback;
}

const searchStyles = {
	option: (base: any) => ({
		...base,
		cursor: 'pointer',
	}),
	indicatorSeparator: (base: any) => ({
		...base,
		display: 'none',
	}),
	dropdownIndicator: (base: any) => ({
		...base,
		display: 'none',
	}),
};

const SearchControl = ({ children, ...props }: any) => (
	<components.Control {...props}>
		<span className="ps-3 mb-1">
			<Search size={13} />
		</span>
		{children}
	</components.Control>
);

const getClientSettingItems = (
	user: Record<string, any>,
	appSettings: Record<string, any>,
	reqAccess: Record<string, any>,
	t: TranslationFunction,
) => {
	return [
		{
			title: t('general'),
			subItems: [
				{
					LinkName: t('companyCalendar'),
					href: '/?page=calendar',
				},
				reqAccess?.ActionPlans_Access?.Status === '0' &&
				reqAccess?.ActionPlans_Access?.View_Allow === 'T'
					? {
						LinkName: t('companyWideActionPlans'),
						href: '/?page=companyactionplans',
					}
					: null,
				user?.ActingAsACCESSLEVEL === 'A' && {
					LinkName: t('companyWideQuickLinks'),
					href: '/?page=company-wide-quick-links',
				},
			],
		},
		{
			title: t('marketingWidget'),
			subItems: [
				{
					LinkName: t('companyWideBlogs'),
					href: '/?page=blogs&publicid=global',
				},
				...(reqAccess?.Campaigns_Access?.Status === '0' &&
				reqAccess?.Campaigns_Access?.View_Company === 'T'
					? [
						{
							LinkName: t('companyWideEmailCampaigns'),
							href: '/?page=companycampaigns',
							...(appSettings?.email_templates_beta
								? {
									badgeData: {
										isVisible: true,
										text: 'Old',
									},
								}
								: {}),
						},
						...(appSettings?.email_templates_beta
							? [
								{
									LinkName: t('companyWideEmailCampaigns'),
									href: '/?page=company-email-campaigns&cat=automations&viewid=all',
									badgeData: {
										isVisible: true,
										text: 'New',
									},
								},
							]
							: []),
						{
							LinkName: t('companyWideSurveys'),
							href: '/?page=companysurveys',
						},
						{
							LinkName: t('content'),
							href: '/?page=content&cat=company',
							...(appSettings?.email_templates_beta
								? {
									badgeData: {
										isVisible: true,
										text: 'Old',
									},
								}
								: {}),
						},
						{
							LinkName: t('contentTemplates'),
							href: '/?page=companycontenttemplates',
							...(appSettings?.email_templates_beta
								? {
									badgeData: {
										isVisible: true,
										text: 'Old',
									},
								}
								: {}),
						},
					]
					: [null]),
			],
		},
		{
			title: t('company'),
			subItems: [
				reqAccess?.AccessGroups_Access?.Status === '0' &&
				reqAccess?.AccessGroups_Access?.View_Allow === 'T'
					? {
						LinkName: t('accessGroups'),
						href: '/?page=accessgroups',
					}
					: null,
				...(user?.ActingAsACCESSLEVEL === 'A'
					? [
						{
							LinkName: t('brand'),
							href: '/?page=brand',
						},
						{
							LinkName: t('companies'),
							href: '/?page=companies',
						},
						{
							LinkName: t('groups'),
							href: '/?page=groups',
						},
					]
					: [null]),
				...(reqAccess?.Offices_Access?.Status === '0' &&
				reqAccess?.Offices_Access?.View_AccessLevel !== 'N'
					? [
						{
							LinkName: t('officeCustomFields'),
							href: '/?page=officescustomfields',
						},
						{
							LinkName: t('offices'),
							href: '/?page=offices',
						},
					]
					: [null]),
				appSettings?.feature_iqvoice === 'T' ?
					{
						LinkName: t('propertyIDs'),
						href: '/?page=systemadmin&cat=propertyid',
					}
					: null,
				{
					LinkName: t('teams'),
					href: '/?page=agentteams',
				},
			],
		},
		{
			title: t('contactsAndLeads'),
			subItems: [
				...(reqAccess?.LeadSources_Access?.Status === '0' &&
				reqAccess?.LeadSources_Access?.View_Allow === 'T'
					? [
						{
							LinkName: t('contactLeadStatusCodes'),
							href: '/?page=statuscodes',
						},
					]
					: [null]),
				user?.ActingAsACCESSLEVEL === 'A'
					? {
						LinkName: t('haltedAgents'),
						href: '/?page=leads&cat=blacklist',
					}
					: null,
				user?.ActingAsACCESSLEVEL === 'A'
					? {
						LinkName: t('leadGroups'),
						href: '/?page=leads&cat=usergroups',
					}
					: null,
				...(reqAccess?.LeadSources_Access?.Status === '0' &&
				reqAccess?.LeadSources_Access?.View_Allow === 'T'
					? [
						{
							LinkName: t('leadSources'),
							href: '/?page=leadsources',
						},
					]
					: [null]),
			],
		},
		{
			title: t('listings'),
			subItems: [
				user?.ActingAsACCESSLEVEL === 'A' && {
					LinkName: t('fieldLabelDisplay'),
					href: '/?page=field-label-display',
				},
				{
					LinkName: t('listingNotifications'),
					href: '/?page=listing-notifications',
				},
			],
		},
		{
			title: t('financial'),
			subItems: [
				// #5023964743 - mirrors client SettingsItem.js; keep in step
				reqAccess?.Transactions_Access?.Create_Change_Checklist === 'T'
					? {
						LinkName: t('checklists'),
						href: '/?page=checklists',
					}
					: null,
				reqAccess?.Transactions_Access?.Create_Change_Lookups === 'T'
					? {
						LinkName: t('lookups'),
						href: '/?page=financial-lookups',
					}
					: null,
				reqAccess?.Transactions_Access?.Create_Change_MessageTemplates === 'T'
					? {
						LinkName: t('messageTemplates'),
						href: '/?page=messagetemplates&publicid=global',
					}
					: null,
				reqAccess?.Transactions_Access?.Create_Change_CommissionPlans === 'T' ||
				reqAccess?.Transactions_Access?.Create_Change_TransactionFeePlans === 'T' ||
				reqAccess?.Transactions_Access?.Create_Change_TransactionDeductionPlans === 'T' ||
				reqAccess?.Transactions_Access?.Create_Change_AgentFeePlans === 'T' ||
				(appSettings?.transactions_servicefeeplans === 'T' &&
					reqAccess?.Transactions_Access?.Create_Change_ServiceFeePlans === 'T')
					? {
						LinkName: t('plans'),
						href: '/?page=financial-plans',
					}
					: null,
				appSettings?.feature_transactions === 'T' &&
				appSettings?.transactions_admin_publicid?.includes(
					user?.ActingAsPUBLICID)
					? {
						LinkName: t('transactionSettings'),
						href: '/?page=systemadmin&cat=managersettings&subcat=maintain&applicationid=global',
					} : null,
			],
		},
		{
			title: t('dashboard'),
			subItems: [
				...(user?.ActingAsACCESSLEVEL === 'A'
					? [
						{
							LinkName: t('announcementBanners'),
							href: '/?page=announcement-banners',
						},
						{
							LinkName: t('customWidgets'),
							href: '/?page=customwidgets',
						},
						{
							LinkName: t('settingsDrawerDashboardDisplay'),
							href: '/?page=dashboard_display',
						},
					]
					: [null]),
			],
		},
		{
			title: t('miscellaneous'),
			subItems: [
				...(user?.ActingAsACCESSLEVEL === 'A'
					? [
						{
							LinkName: t('domains'),
							href: '/?page=customersdomains',
						},
					]
					: [null]),
				reqAccess?.Vendors_Access?.Status === '0' &&
				reqAccess?.Vendors_Access?.View_Allow === 'T'
					? {
						LinkName: t('vendors'),
						href: '/?page=vendors',
					}
					: null,
			],
		},
	];
};

const StyledDiv = styled.div`
	.dropdown-section {
		padding-top: 16px;
		font-weight: 500;
		cursor: default;
		text-transform: uppercase;

		&:not(:first-of-type) {
			margin-top: 25px;
		}
	}
`;

const StyledList = styled.ul`
	list-style: none;
	padding-left: 0;

	// 28px top inset isn't a clean Bootstrap step at this repo's 14px root, so
	// it's a literal value; bottom stays at the old 14px.
	&.my-settings {
		padding-top: 28px;
		padding-bottom: 14px;
	}

	> li {
		margin: 0;
		display: flex;
		align-items: center;
		// min-height + flex centring hits the row height exactly regardless of
		// line-height quirks.
		min-height: 40px;
		padding-left: 40px;
	}

	// Type comes from the fs-6 fw-normal lh-base utilities on .my-settings and is
	// inherited here, so the list and its nested sub-lists stay in one place.
	> li > a {
		padding: 0;
		text-decoration: none;
		font-stretch: normal;
		font-style: normal;
		letter-spacing: normal;
		color: var(--bs-dark);
		background-color: unset;
		transition: 0.25s all ease-out;
	}

	> li > a:hover,
	> li > a:active {
		color: var(--bs-pink);
		text-decoration: none;
		transition: 0.25s all ease-out;
	}
`;

const ClientSettingsWrapper = styled.div`
	.settingsLink:hover {
		text-decoration: underline !important;
	}
`;

const SettingsMenu = ({ settingsOptions, t = (key: string) => key, platformType }: SettingsMenuProps) => {
	const {
		accountType,
		appSettings,
		isAutomationAvailable,
		isEnterprise,
		isEnterpriseChild,
		isFbLimited,
		ownBilling,
		showAnnouncementBanners,
		showLandingPages,
		showListingOverlays,
		reqAccess,
		settingItems: settingItemsProp,
		user,
	} = settingsOptions || {};

	const settingItems = useMemo(() => {
		if (Array.isArray(settingItemsProp)) {
			return settingItemsProp;
		}

		if (platformType !== 'client') {
			return [];
		}
		return getClientSettingItems(user, appSettings, reqAccess, t);
	}, [settingItemsProp, platformType, user, appSettings, reqAccess, t]);

	const sortedLinkArray = useMemo(() => {
		const linkArray = [];

		settingItems?.forEach((item) => {
			item?.subItems?.forEach((subItem) => {
				if (subItem?.LinkName && subItem?.href) {
					linkArray.push({ LinkName: subItem.LinkName, href: subItem.href });
				}
			});
		});

		return linkArray.sort((a, b) => (a?.LinkName > b?.LinkName ? 1 : -1));
	}, [settingItems]);

	if (platformType === 'client') {
		return (
			<ClientSettingsWrapper className="d-flex flex-column h-100">
				<div className="bay-grid p-md-5 px-3 py-4 flex-grow-1 overflow-auto">
					<div className="row d-flex align-items-center ps-md-3 pe-md-2 pb-5">
						<div className="col-12 col-md-8 p-md-0 pb-3">
							{t('settingsDrawerDescription')}
						</div>
						<div className="col-12 col-md-4 pe-md-0 pb-0">
							<Select
								escapeClearsValue
								isClearable
								isSearchable
								closeMenuOnSelect={false}
								components={{ Control: SearchControl }}
								options={sortedLinkArray.map((linkItem) => ({
									label: linkItem.LinkName,
									value: linkItem.href,
								}))}
								onChange={(selectedOption) => {
									if (selectedOption?.value) {
										window.location.assign(selectedOption?.value);
									}
								}}
								placeholder={t('search')}
								styles={searchStyles}
							/>
						</div>
					</div>
					<div className="d-flex row flex-row flex-wrap">
						{settingItems && settingItems?.map((item) => {
							const validSubItem = item?.subItems?.filter((subItem) => subItem !== null);
							if (!validSubItem.length) {
								return;
							}
							return (
								<div key={item?.title} className="col-12 col-sm-6 col-md-4 pe-md-2 pb-4">
									<Card className="h-100">
										<Card.Body className="px-4 pb-1">
											<Card.Title className="fw-bold mb-3">{item?.title}</Card.Title>
											<div className="d-flex flex-column">
												{item?.subItems?.map((subItem, index) => {
													return (subItem && subItem !== null && ((showAnnouncementBanners && subItem?.href === '/?page=announcement-banners') || subItem?.href !== '/?page=announcement-banners') && (
														<div key={index} className="d-flex align-items-center gap-3 mb-2">
															<a className="settingsLink text-primary lh-base" href={subItem?.href}>
																{subItem?.LinkName}
															</a>
															{subItem?.badgeData?.isVisible && (
																<BayBadge
																	className="lh-base fs-6 fw-bold px-2 py-1 rounded-1"
																	bg={subItem?.badgeData?.text?.toLowerCase() === 'old' ? 'orange' : 'pink'}
																	bgValue={subItem?.badgeData?.text?.toLowerCase() === 'old' ? '200' : '100'}
																	text={subItem?.badgeData?.text?.toLowerCase() === 'old' ? 'orange' : 'pink'}
																	textValue={subItem?.badgeData?.text?.toLowerCase() === 'old' ? '800' : '700'}
																>
																	{subItem?.badgeData?.text}
																</BayBadge>
															)}
														</div>
													));
												})}
											</div>
										</Card.Body>
									</Card>
								</div>
							);
						})}
					</div>
				</div>
			</ClientSettingsWrapper>
		);
	}

	return settingsOptions ? (
		<StyledDiv>
			{/* Figma: Desktop/Body/Regular - Inter 1rem, weight 400, line height 150%.
			    fs-6 is 1rem; fs-7 (0.875rem) would render a step smaller. */}
			<StyledList className="my-settings fs-6 fw-normal lh-base pe-5">
				<li>
					<a href="/settings/profile">Profile</a>
				</li>
				<li>
					<a href="/settings/brand">Brand</a>
				</li>
				<li>
					<a href="/settings/content">Content</a>
				</li>
				{showLandingPages && (
					<li>
						<a href="/settings/landing-pages">Landing Pages</a>
					</li>
				)}
				{showListingOverlays ? (
					<li className="settings-accordion-item p-0">
						<Accordion className="settings-collapsible" flush>
							<Accordion.Item eventKey="0">
								<Accordion.Header className="fs-6 fw-normal lh-base">{t('listings')}</Accordion.Header>
								<Accordion.Body className="py-0">
									<StyledList>
										<li>
											<a href="/settings/listings">{t('general')}</a>
										</li>
										<li>
											<a href="/settings/listings/overlays">{t('listingOverlays')}</a>
										</li>
									</StyledList>
								</Accordion.Body>
							</Accordion.Item>
						</Accordion>
					</li>
				) : (
					<li>
						<a href="/settings/listings">{t('listings')}</a>
					</li>
				)}
				{!isEnterprise ? (
					isEnterpriseChild && isAutomationAvailable ? (
						<li className="settings-accordion-item p-0">
							<Accordion className="settings-collapsible" flush>
								<Accordion.Item eventKey="0">
									<Accordion.Header className="fs-6 fw-normal lh-base">{t('listingAutomation')}</Accordion.Header>
									<Accordion.Body className="py-0">
										<StyledList>
											<li>
												<a href="/settings/listing-automation">{t('yourListings')}</a>
											</li>
											<li>
												<a href="/settings/company-listing-automation">
													{t('companyListings')}
												</a>
											</li>
										</StyledList>
									</Accordion.Body>
								</Accordion.Item>
							</Accordion>
						</li>
					) : (
						<li>
							<a href="/settings/listing-automation">{t('listingAutomation')}</a>
						</li>
					)
				) : null}
				{isEnterprise || !isFbLimited ? (
					<li>
						<a href="/settings/ads">{t('advertising')}</a>
					</li>
				) : null}
				{isEnterprise || (isEnterpriseChild && accountType === 'broker') ? (
					<li>
						<a href="/settings/compliance">Compliance</a>
					</li>
				) : null}
				<li>
					<a href="/settings/notifications">Notifications</a>
				</li>
				{ownBilling && (
					<li>
						<a href="/settings/billing">Billing</a>
					</li>
				)}
			</StyledList>
		</StyledDiv>
	) : (
		<StyledDiv>
			<StyledList className="my-settings pe-5">
				<li className="dropdown-section">
					<p>No settings to show</p>
				</li>
			</StyledList>
		</StyledDiv>
	);
};

export default SettingsMenu;
